import React, {useMemo, useState} from 'react';
import axios from 'axios';
import '../styles/SubscriptionTable.css';
import { toast } from "../../utils/toast";

const SubscriptionTable = ({ products, subscriptions }) => {
    const [initsubscriptions, setSubscriptions] = useState(subscriptions);
    const [itemList, setItemList] = useState(products);
    const [sortKey, setSortKey] = useState(''); // 정렬 기준
    const [sortOrder, setSortOrder] = useState('asc'); // 정렬 순서 ('asc', 'desc')
    const [filterStatus, setFilterStatus] = useState('all'); // 사용 여부 필터 ('all', 'active', 'inactive')
    const [alertVisible, setAlertVisible] = useState(false); // Alert 창 표시 상태
    const [selectedMacAddress, setSelectedMacAddress] = useState(null); // 삭제 대상 MAC Address
    const [selectedItemId, setSelectedItemId] = useState(null); // 삭제 대상 Item ID
    const [selectedHashKey, setSelectedHashKey] = useState(null); // 삭제 대상 Hash Key

    console.log('상품 리스트:', products);

    // 필터링 및 정렬 로직
    const getFilteredAndSortedSubscriptions = () => {
        const filteredSubscriptions = initsubscriptions.filter((sub) => {
            if (filterStatus === 'all') return true;
            return filterStatus === 'active' ? sub.useState === 'active' : sub.useState === 'inactive';
        });

        return filteredSubscriptions.sort((a, b) => {
            let valueA, valueB;

            switch (sortKey) {
                case 'name':
                    valueA = a.buyerName.toLowerCase();
                    valueB = b.buyerName.toLowerCase();
                    break;
                case 'creationDate':
                    valueA = new Date(a.creationDate);
                    valueB = new Date(b.creationDate);
                    break;
                case 'expirationDate':
                    valueA = new Date(a.expirationDate);
                    valueB = new Date(b.expirationDate);
                    break;
                case 'productNumber':
                    valueA = a.productNumber;
                    valueB = b.productNumber;
                    break;
                case 'useState':
                    valueA = a.useState.toLowerCase();
                    valueB = b.useState.toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
            } else {
                return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
            }
        });
    };

    const filteredAndSortedSubscriptions = getFilteredAndSortedSubscriptions();

    const handleDeleteClick = (itemId, hashKey, macAddress) => {
        setSelectedItemId(itemId);
        setSelectedHashKey(hashKey);
        setSelectedMacAddress(macAddress);
        setAlertVisible(true); // Alert 창 표시
    };

    const handleConfirmDelete = async () => {
        if (selectedItemId && selectedHashKey && selectedMacAddress) {
            try {
                const response = await axios.delete('/api/subscription/hash-key-mac-delete/temp', {
                    params: {
                        id: selectedItemId,
                        hashKey: selectedHashKey,
                        macAddress: selectedMacAddress,
                    },
                });

                if (response.data.result === 'success') {
                    toast('MAC 주소 삭제 성공');

                    // 삭제된 데이터를 UI에서 제거
                    const updatedSubscriptions = subscriptions.map((sub) => {
                        if (sub.productNumber === selectedItemId) {
                            // 해당 구독 항목의 macAddress를 제거
                            return {
                                ...sub,
                                macAddress1: sub.macAddress1 === selectedMacAddress ? null : sub.macAddress1,
                                macAddress2: sub.macAddress2 === selectedMacAddress ? null : sub.macAddress2,
                            };
                        }
                        return sub;
                    });

                    // 새로운 구독 데이터로 상태 업데이트
                    setSubscriptions(updatedSubscriptions);
                } else {
                    toast(`삭제 실패: ${response.data.message}`);
                }
            } catch (error) {
                console.error('삭제 요청 실패:', error);
                toast('MAC 주소 삭제 요청에 실패했습니다.');
            }
        }

        setAlertVisible(false);
        setSelectedItemId(null);
        setSelectedHashKey(null);
        setSelectedMacAddress(null);
    };

    const handleCancelDelete = () => {
        setAlertVisible(false);
        setSelectedItemId(null);
        setSelectedHashKey(null);
        setSelectedMacAddress(null);
    };

    const handleCopyHashKey = (hashKey) => {
        navigator.clipboard.writeText(hashKey)
            .then(() => {
                toast('Hash Key가 클립보드에 복사되었습니다.');
            })
            .catch((err) => {
                console.error('Hash Key 복사 실패:', err);
            });
    }

    // itemList를 Map으로 변환하여 성능 최적화
    const itemMap = useMemo(() => {
        const map = new Map();
        products.forEach(item => {
            map.set(item.id, item.name);
        });
        return map;
    }, [products]);

    return (
        <div className="sch-adminMain">
            <div className="sch-adminHead">
                <h2 className="sc-h2">구독 목록</h2>
            </div>

            <div className="sch-filterbar">
                <label className="sc-field">
                    <span className="sc-field__label">정렬 기준</span>
                    <select className="sc-select" onChange={(e) => setSortKey(e.target.value)} defaultValue="">
                        <option value="" disabled>정렬 기준 선택</option>
                        <option value="name">이름</option>
                        <option value="creationDate">날짜</option>
                        <option value="expirationDate">만료 날짜</option>
                        <option value="productNumber">상품 번호</option>
                        <option value="useState">사용 여부</option>
                    </select>
                </label>

                <label className="sc-field">
                    <span className="sc-field__label">정렬 순서</span>
                    <select className="sc-select" onChange={(e) => setSortOrder(e.target.value)} defaultValue="asc">
                        <option value="asc">오름차순</option>
                        <option value="desc">내림차순</option>
                    </select>
                </label>

                <label className="sc-field">
                    <span className="sc-field__label">사용 여부</span>
                    <select className="sc-select" onChange={(e) => setFilterStatus(e.target.value)} defaultValue="all">
                        <option value="all">모두</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </label>
            </div>

            <div className="sc-tablewrap">
                <table className="sc-table sc-table--responsive">
                    <thead>
                        <tr>
                            <th>구매자 이름</th>
                            <th>Hash Key</th>
                            <th>날짜</th>
                            <th>만료 일자</th>
                            <th>MAC Address 1</th>
                            <th>MAC Address 2</th>
                            <th className="sc-td--num">상품 번호</th>
                            <th>상품명</th>
                            <th>사용 여부</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedSubscriptions.map((sub, index) => (
                            <tr key={index}>
                                <td data-label="구매자 이름">{sub.buyerName}</td>
                                <td data-label="Hash Key">
                                    <button
                                        type="button"
                                        className="sch-copykey"
                                        onClick={() => handleCopyHashKey(sub.hashKey)}
                                        title="클릭하면 복사됩니다"
                                    >
                                        {sub.hashKey}
                                    </button>
                                </td>
                                <td data-label="날짜" className="sc-td--muted">{new Date(sub.creationDate).toLocaleDateString()}</td>
                                <td data-label="만료 일자" className="sc-td--muted">{new Date(sub.expirationDate).toLocaleDateString()}</td>
                                <td data-label="MAC Address 1">
                                    <span className="sch-macCell">
                                        {sub.macAddress1 || 'N/A'}
                                        {sub.macAddress1 && (
                                            <button
                                                type="button"
                                                className="sc-btn sc-btn--danger sc-btn--sm"
                                                onClick={() => handleDeleteClick(sub.productNumber, sub.hashKey, sub.macAddress1)}
                                            >
                                                삭제
                                            </button>
                                        )}
                                    </span>
                                </td>
                                <td data-label="MAC Address 2">
                                    <span className="sch-macCell">
                                        {sub.macAddress2 || 'N/A'}
                                        {sub.macAddress2 && (
                                            <button
                                                type="button"
                                                className="sc-btn sc-btn--danger sc-btn--sm"
                                                onClick={() => handleDeleteClick(sub.productNumber, sub.hashKey, sub.macAddress2)}
                                            >
                                                삭제
                                            </button>
                                        )}
                                    </span>
                                </td>
                                <td data-label="상품 번호" className="sc-td--num">{sub.productNumber}</td>
                                <td data-label="상품명">{itemMap.get(sub.productNumber) || 'N/A'}</td>
                                <td data-label="사용 여부">
                                    <span className={`sc-badge ${String(sub.useState).toLowerCase() === 'active' ? 'sc-badge--paid' : 'sc-badge--done'}`}>
                                        {sub.useState}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredAndSortedSubscriptions.length === 0 && (
                    <div className="sc-empty">
                        <div className="sc-empty__title">구독 내역이 없습니다</div>
                    </div>
                )}
            </div>

            {alertVisible && (
                <div className="sc-modal-backdrop">
                    <div className="sc-modal">
                        <div className="sc-modal__head">
                            <div className="sc-modal__title">MAC 주소 삭제</div>
                            <button type="button" className="sc-modal__x" onClick={handleCancelDelete} aria-label="닫기">✕</button>
                        </div>
                        <div className="sc-modal__body">{selectedMacAddress}를 삭제하시겠습니까?</div>
                        <div className="sc-modal__foot">
                            <button type="button" className="sc-btn sc-btn--ghost" onClick={handleCancelDelete}>취소</button>
                            <button type="button" className="sc-btn sc-btn--danger" onClick={handleConfirmDelete}>삭제</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionTable;
